import React, { memo } from "react";

import styled from "@emotion/styled";
import classNames from "classnames";

import Icon from "../Icon";
import { Link } from "../LinkComponent";

const BoxImage = styled.div`
    display: block;
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    border-radius: 6px 6px 0 0;

    > span {
        display: block;
        padding-bottom: 66.666%;
    }

    /* This image is just there to enforce the minimum height. */
    > img {
        display: block;
        visibility: hidden;
    }

    &.BoxImage--portrait {
        > span {
            padding-bottom: 133.333%;
        }
    }

    &.BoxImage--header {
        > span {
            padding-bottom: 50%;
        }
    }

    &.BoxImage--slimHeader {
        > span {
            padding-bottom: 25%;
        }
    }

    &.BoxImage--full {
        &:last-child {
            flex-grow: 1;
        }

        img {
            width: 100%;
        }
    }

    &:last-child {
        border-radius: 6px;
    }
`;

const BoxVideo = styled.div`
    position: relative;
    padding-bottom: 62.5%;
    overflow: hidden;
    border-radius: 6px 6px 0 0;

    > iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
    }

    &:last-child {
        border-radius: 6px;
    }
`;

const BoxChildren = styled.div`
    ${BoxImage} {
        margin-right: -1.25rem;
        margin-left: -1.25rem;

        &:first-of-type {
            margin-top: -1.25rem;
        }

        &:last-child {
            margin-bottom: -1.25rem;
        }

        &:not(:last-child) {
            margin-bottom: 1.25rem;
        }
    }

    ${BoxVideo} {
        margin-right: -1.25rem;
        margin-left: -1.25rem;

        &:first-of-type {
            margin-top: -1.25rem;
        }

        &:last-child {
            margin-bottom: -1.25rem;
        }

        &:not(:last-child) {
            margin-bottom: 1.25rem;
        }
    }

    h1 {
        margin-bottom: 1rem;
        font-weight: normal;
    }

    h2 {
        font-size: 1em;
    }

    h3 {
        font-size: 1em;
    }

    h4 {
        margin-top: 1em;
        font-size: 0.8em;
    }

    a:not(.button) {
        text-decoration: underline;
    }
`;

const Box_ = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0;
    overflow: hidden;

    ${BoxChildren} {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        padding: 1.25rem;
    }

    &.box--clickable {
        cursor: pointer;
    }

    &.box--leftImage {
        > ${BoxImage} {
            align-self: flex-start;
            border-radius: 6px 0 0 6px;
        }

        flex-direction: row;

        > :first-of-type {
            width: var(--horizontal-box-split);
        }

        > :last-child {
            width: calc(100% - var(--horizontal-box-split));
        }
    }
`;

const Box = memo(
    ({ className, leftImage, children, scrollToAnchor, ...boxProps }) => {
        const _children = React.Children.toArray(children);

        let firstChild = null;
        let contentChildren = _children;

        const inlineStyles = { "--horizontal-box-split": "33%" };
        if (leftImage) {
            firstChild = _children[0];
            contentChildren = _children.slice(1);

            if (typeof leftImage === "number") {
                inlineStyles["--horizontal-box-split"] = `${leftImage}%`;
            }
        }

        return (
            <Box_
                style={inlineStyles}
                className={classNames(
                    "box",
                    leftImage && "box--leftImage",
                    className,
                    boxProps.onClick && "box--clickable"
                )}
                {...boxProps}
            >
                {scrollToAnchor && (
                    <div
                        id={scrollToAnchor}
                        style={{
                            marginTop: "-70px",
                            paddingTop: "70px",
                        }}
                    ></div>
                )}
                {firstChild}
                {contentChildren.length > 0 && (
                    <BoxChildren>{contentChildren}</BoxChildren>
                )}
            </Box_>
        );
    }
);
Box.displayName = "Box";

Box.Image = memo(
    ({
        href,
        target,
        src,
        format,
        path,
        bgPosition = null,
        height,
        gtag,
        externalLink = false,
        className = false,
    }) => {
        if (path) {
            src = path;
        }

        const style = { backgroundImage: `url(${src})` };
        if (bgPosition !== null) {
            style.backgroundPosition = bgPosition;
        }

        const containerProps = {
            href,
            target,
            style,
        };

        if (gtag) {
            containerProps["data-gtag"] = gtag;
        }

        let image = <span />;
        if (format === "full") {
            image = (
                <img
                    style={{
                        ...(height ? { height: `${height}px` } : {}),
                    }}
                    src={src}
                    alt=""
                />
            );
        }

        let ImageContainer = "span";
        if (containerProps.href) {
            if (containerProps.href.startsWith("/") && !externalLink) {
                containerProps.to = containerProps.href;
                delete containerProps.href;
                ImageContainer = Link;
            } else {
                ImageContainer = "a";
                containerProps.target = "_blank";
                containerProps.rel = "noopener noreferrer";
            }
        }

        return (
            <BoxImage
                renderAs={ImageContainer}
                className={classNames(`BoxImage--${format}`, className)}
                {...containerProps}
            >
                {image}
            </BoxImage>
        );
    }
);
Box.Image.displayName = "Box.Image";

Box.Video = memo(({ src }) => {
    return (
        <BoxVideo>
            <iframe allowFullScreen="1" frameBorder="0" src={src}></iframe>
        </BoxVideo>
    );
});
Box.Video.displayName = "Box.Video";

const IconContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: -1.25rem;
    margin-left: -1.25rem;
    background-color: $primary;

    &:first-of-type {
        margin-top: -1.25rem;
    }

    &:not(:last-child) {
        margin-bottom: 1.25rem;
    }
`;

const StyledIcon = styled(Icon)`
    width: 80px;
    height: 80px;
`;

Box.Icon = memo(({ type }) => {
    return (
        <IconContainer>
            <StyledIcon type={type} />
        </IconContainer>
    );
});
Box.Icon.displayName = "Box.Icon";

const BoxContent = styled.div`
    &.box--pushDown {
        margin-top: auto;
    }
`;

Box.Content = memo(({ pushDown, children, className = "" }) => (
    <BoxContent className={classNames(pushDown && "box--pushDown", className)}>
        {children}
    </BoxContent>
));
Box.Content.displayName = "Box.Content";

export default Box;
